"use client";

import { useState } from "react";
import { Loader, Mail, Phone, Instagram } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  const [consent, setConsent] = useState(false);
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center text-center py-20 px-8 transition-opacity duration-700">
        <div className="w-4 h-4 mb-8" style={{
        backgroundColor: "var(--primary)",
        transform: "rotate(0deg)"
      }} aria-hidden="true" />
        <p className="font-default font-semibold text-2xl md:text-3xl text-foreground leading-relaxed max-w-md">
          Your request for a Legacy review has been received.
        </p>
        <p className="mt-6 text-xs tracking-[0.2em] uppercase text-muted-foreground font-mono">
          — Legacy Wealth Legal
        </p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="full_name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Alexander Sterling" {...field} className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default text-lg" />
            </FormControl>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="sterling@wealth.com" {...field} className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default text-lg" />
            </FormControl>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Business Entity <span className="normal-case tracking-normal text-muted-foreground/60">(optional)</span>
            </FormLabel>
            <FormControl>
              <Input placeholder="Family Office or Corporation" {...field} className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default text-lg" />
            </FormControl>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Legal Interest
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg w-full data-[placeholder]:text-muted-foreground/40">
                  <SelectValue placeholder="Select a practice area" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-md border-border bg-background">
                <SelectItem value="Estate Planning">Estate Planning</SelectItem>
                <SelectItem value="Asset Protection">Asset Protection</SelectItem>
                <SelectItem value="Revocable Trusts">Revocable Trusts</SelectItem>
                <SelectItem value="Family Safeguards">Family Safeguards</SelectItem>
                <SelectItem value="Wealth Preservation">Wealth Preservation</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please share a few words about your objectives",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Legacy Objectives
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your wealth preservation goals..." {...field} style={{
          minHeight: "200px"
        }} className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 pt-2 focus-visible:ring-0 focus-visible:border-primary text-foreground placeholder:text-muted-foreground/40 font-default text-lg resize-none" />
            </FormControl>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem className="gap-1">
            <FormLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground font-mono font-medium">
              Urgency Level
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="rounded-md border-0 border-b border-slate-muted bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-primary text-foreground font-default text-lg w-full data-[placeholder]:text-muted-foreground/40">
                  <SelectValue placeholder="Select your timeline" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="rounded-md border-border bg-background">
                <SelectItem value="Immediate">Immediate Action</SelectItem>
                <SelectItem value="Next 30 Days">Within 30 Days</SelectItem>
                <SelectItem value="Quarterly">This Quarter</SelectItem>
                <SelectItem value="Planning">Strategic Planning</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="text-xs font-mono text-destructive" />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="consent" checked={consent} onCheckedChange={v => setConsent(Boolean(v))} className="mt-1 border-slate-muted data-[state=checked]:bg-primary data-[state=checked]:border-primary rounded-md" />
        <Label htmlFor="consent" className="text-sm text-foreground/80 font-default leading-relaxed cursor-pointer">
          I consent to receiving strategic updates from Legacy Wealth Legal.
        </Label>
      </div>

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-md">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} style={{
      height: "60px"
    }} className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md uppercase tracking-[0.3em] text-xs font-mono font-semibold mt-4 shadow-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Processing...
          </> : <>Schedule Legacy review →</>}
      </Button>
    </Form>;
}
export default function ScheduleYourLegacyAudit() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="4358" id="schedule-your-legacy-audit" className="bg-background text-foreground py-20 md:py-32 px-6 md:px-12 lg:px-20">
      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-10">
                <div className="w-[40px] h-[1px] bg-primary" />
                <p className="text-[10px] md:text-xs tracking-[0.35em] uppercase text-primary font-mono"><br /></p>
              </div>

              <h2 className="font-serif font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground mb-10">
                Secure
                <br />
                your family's
                <br />
                <em className="italic text-primary">future.</em>
              </h2>

              <p className="font-default text-lg md:text-xl text-slate-muted leading-relaxed max-w-md mb-14">
                Protecting your wealth requires more than a document; it requires a strategy. Our Legacy review identifies vulnerabilities in your current estate plan and provides a roadmap for total asset protection.
              </p>

              <div className="space-y-0 max-w-md">
                <Separator className="bg-border/60" />
                <div className="flex items-center gap-4 py-5"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-4 text-primary shrink-0" aria-hidden="true" data-ast-id="f:/src/pages/Contact/sections/ScheduleYourLegacyAudit.tsx|sig:n4x4od" data-ast-start="10778" data-ast-end="10828"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg><span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-mono w-16" data-ast-id="f:/src/pages/Contact/sections/ScheduleYourLegacyAudit.tsx|sig:g0gy6z" data-ast-start="10847" data-ast-end="10994">Direct</span><span className="font-mono text-sm text-foreground tracking-wider" data-ast-id="f:/src/pages/Contact/sections/ScheduleYourLegacyAudit.tsx|sig:h7dwjy" data-ast-start="11013" data-ast-end="11142">+1 305 317 2566</span></div>
                <Separator className="bg-border/60" />
                <div className="flex items-center gap-4 py-5">
                  <Mail className="size-4 text-primary shrink-0" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground font-mono w-16">
                    Secure
                  </span>
                  <span className="font-mono text-sm text-foreground tracking-wider break-all">contact@legacywealthlegal.com</span>
                </div>
                <Separator className="bg-border/60" />
                
                <Separator className="bg-border/60" />
              </div>
            </div>

            <p className="text-[10px] tracking-[0.35em] uppercase text-muted-foreground font-mono mt-16 lg:mt-24">
              Legacy Wealth Legal — Florida, USA
            </p>
          </div>

          {}
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="bg-card border border-border p-8 md:p-12 lg:p-14 rounded-md" style={{
            boxShadow: "var(--shadow-lg)"
          }}>
              <div className="mb-10">
                <p className="text-[10px] tracking-[0.35em] uppercase text-primary font-mono mb-4">
                  Confidential Intake
                </p>
                <h3 className="font-serif font-default text-2xl md:text-3xl text-foreground">
                  Request Your Legacy review
                </h3>
              </div>

              <FormProvider formKey="legacy_audit_form_contact" formId="legacy_audit_form_contact" sectionName="schedule-your-legacy-audit" defaultValues={{
              full_name: "",
              email: "",
              company: "",
              inquiry_type: "",
              message: "",
              timeline: ""
            }} validationRules={{
              full_name: {
                required: true,
                minLength: 2
              },
              email: {
                required: true,
                pattern: "email"
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Schedule Legacy review" successMessage="Your request for a Legacy review has been received." errorMessage="We could not deliver your request. Please contact our office directly.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}